import { useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'

/** frameloop="never" の Canvas を指定 fps 以下で更新する。 */
export const useRenderFrameLoop = (frameRateCap: number) => {
  const advance = useThree((state) => state.advance)
  const elapsedSecondsRef = useRef(0)

  useEffect(() => {
    const frameIntervalMs = 1000 / frameRateCap
    let frameId = 0
    let previousTickAt: number | null = null
    let lastRenderedAt: number | null = null
    let accumulatedMs = 0

    const resetTiming = () => {
      previousTickAt = null
      lastRenderedAt = null
      accumulatedMs = 0
    }

    const onVisibilityChange = () => {
      if (document.hidden) resetTiming()
    }

    const tick = (now: number) => {
      frameId = requestAnimationFrame(tick)
      if (document.hidden) return

      if (previousTickAt === null) {
        previousTickAt = now
        lastRenderedAt = now
        advance(elapsedSecondsRef.current)
        return
      }

      accumulatedMs += now - previousTickAt
      previousTickAt = now
      // rAF の時刻には微小な揺れがあるため、60 Hz で 30 fps に落ちないよう 1 ms 許容する。
      if (accumulatedMs + 1 < frameIntervalMs) return

      accumulatedMs = Math.max(0, accumulatedMs - frameIntervalMs)
      if (accumulatedMs >= frameIntervalMs) accumulatedMs %= frameIntervalMs
      elapsedSecondsRef.current += (now - (lastRenderedAt ?? now)) / 1000
      lastRenderedAt = now
      advance(elapsedSecondsRef.current)
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    frameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frameId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [advance, frameRateCap])
}
